import React from 'react'
import './availability_calendar.css'

// Faithful React port of tm2-tutors' AvailabilityCalendar.svelte (desktop
// layout). 1:1 translation of the markup, classes, geometry (16px per 15-min
// slot, 64px time gutter), striped backgrounds, both 'lesson' and
// 'availability' modes, and the drag-resize / click-to-add interaction. The
// parent owns the cell Sets and reacts to the dispatched callbacks, exactly
// like AvailabilityStep.svelte.

const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const defaultStartHour = 7
const defaultEndHour = 22

function fmtMin(minutes) {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}
function fmtMin12(minutes) {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  const period = h >= 12 ? 'pm' : 'am'
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h
  return `${h12}:${String(m).padStart(2, '0')}${period}`
}
function parseHHMM(time) {
  const [h, m] = time.split(':').map(Number)
  return h * 60 + m
}
function formatDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function cellKey(dayOrdinal, minutes) {
  return `${dayOrdinal}:${minutes}`
}

function AvailabilityCalendar(props) {
  const {
    initialCalendarData,
    customerPreferredCells = new Set(),
    availabilityCells = new Set(),
    selectedLessonCells = new Set(),
    mode = 'lesson',
    tutorName = '',
    lessonDuration = 60,
    singleDayIndex = null,
    onSelectPreferredBlock,
    onResizePreferredBlock,
    onOutsideAvailability,
  } = props

  const calendarData = initialCalendarData

  const gridRef = React.useRef(null)
  const draggingRef = React.useRef(null)
  const didDragRef = React.useRef(false)
  const ctxRef = React.useRef({})
  const cbRef = React.useRef({})
  const [, forceRender] = React.useReducer((x) => x + 1, 0)
  const [expandTop, setExpandTop] = React.useState(0)
  const [expandBottom, setExpandBottom] = React.useState(0)

  const setDragging = (val) => {
    draggingRef.current = val
    forceRender()
  }
  const dragging = draggingRef.current

  cbRef.current = { onSelectPreferredBlock, onResizePreferredBlock, onOutsideAvailability }

  // ---- Visible range / slots ----------------------------------------------
  const visibleRange = (() => {
    let minMin = Infinity
    let maxMin = 0
    const scanCells = (cells) => {
      for (const key of cells) {
        const minutes = parseInt(key.split(':')[1], 10)
        if (minutes < minMin) minMin = minutes
        if (minutes + 15 > maxMin) maxMin = minutes + 15
      }
    }
    scanCells(customerPreferredCells)
    scanCells(selectedLessonCells)
    if (mode === 'availability') scanCells(availabilityCells)
    if (minMin < Infinity) {
      const baseStart = Math.max(0, Math.floor((minMin - 60) / 60) * 60)
      const baseEnd = Math.min(24 * 60, Math.ceil((maxMin + 60) / 60) * 60)
      const start = Math.max(0, Math.min(baseStart, defaultStartHour * 60) - expandTop)
      const end = Math.min(24 * 60, Math.max(baseEnd, defaultEndHour * 60) + expandBottom)
      return { start, end }
    }
    return {
      start: Math.max(0, defaultStartHour * 60 - expandTop),
      end: Math.min(24 * 60, defaultEndHour * 60 + expandBottom),
    }
  })()

  const canExpandEarlier = visibleRange.start > 0
  const canExpandLater = visibleRange.end < 24 * 60

  const slots = (() => {
    const result = []
    for (let m = visibleRange.start; m < visibleRange.end; m += 15) {
      result.push({ minutes: m, isHour: m % 60 === 0, isHalf: m % 60 === 30 })
    }
    return result
  })()

  const endLabel = visibleRange.end % 60 === 0 && visibleRange.end <= 24 * 60 ? fmtMin12(visibleRange.end) : null

  const weekStart = calendarData?.week_start
  const weekDates = (() => {
    const dates = []
    if (!weekStart) return dates
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart + 'T00:00:00')
      d.setDate(d.getDate() + i)
      dates.push(formatDate(d))
    }
    return dates
  })()

  // ---- Lessons / tutoring hours --------------------------------------------
  function getLessonForSlot(dayIndex, minutes) {
    if (!calendarData?.lessons || !weekDates[dayIndex]) return null
    const date = weekDates[dayIndex]
    const slotStart = new Date(`${date}T${fmtMin(minutes)}:00`)
    const slotEnd = new Date(`${date}T${fmtMin(minutes + 15)}:00`)
    const idx = calendarData.lessons.findIndex((l) => {
      const start = new Date(l.start_time)
      const end = new Date(l.end_time)
      return start < slotEnd && end > slotStart
    })
    if (idx === -1) return null
    return calendarData.lessons[idx]
  }
  function isLessonSlot(dayIndex, minutes) {
    return getLessonForSlot(dayIndex, minutes) !== null
  }
  function isTutoringSlot(dayIndex, minutes) {
    if (!calendarData?.tutoring_hours) return false
    const wday = dayIndex === 6 ? 0 : dayIndex + 1
    return calendarData.tutoring_hours.some((a) => {
      const aDow = a.day_of_week % 7
      if (aDow !== wday) return false
      const sMin = parseHHMM(a.start_time)
      let eMin = parseHHMM(a.end_time)
      if (eMin <= sMin) eMin = 24 * 60
      return minutes >= sMin && minutes < eMin
    })
  }

  const tutoringCells = (() => {
    const cells = new Set()
    if (!calendarData?.tutoring_hours) return cells
    for (const a of calendarData.tutoring_hours) {
      const aDow = a.day_of_week % 7
      const dayIndex = aDow === 0 ? 6 : aDow - 1
      const sMin = parseHHMM(a.start_time)
      let eMin = parseHHMM(a.end_time)
      if (eMin <= sMin) eMin = 24 * 60
      for (let m = sMin; m < eMin; m += 15) cells.add(`${dayIndex}:${m}`)
    }
    return cells
  })()

  // ---- Block builders ------------------------------------------------------
  function buildCellBlocks(cellSet) {
    const blocks = []
    for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
      const dayOrdinal = dayIndex + 1
      let blockStartMin = null
      for (const slot of slots) {
        if (cellSet.has(cellKey(dayOrdinal, slot.minutes))) {
          if (blockStartMin === null) blockStartMin = slot.minutes
        } else if (blockStartMin !== null) {
          const startIdx = slots.findIndex((s) => s.minutes === blockStartMin)
          blocks.push({ dayIndex, startIdx, slotCount: (slot.minutes - blockStartMin) / 15, startMin: blockStartMin, endMin: slot.minutes })
          blockStartMin = null
        }
      }
      if (blockStartMin !== null) {
        const startIdx = slots.findIndex((s) => s.minutes === blockStartMin)
        const endMin = slots[slots.length - 1].minutes + 15
        blocks.push({ dayIndex, startIdx, slotCount: (endMin - blockStartMin) / 15, startMin: blockStartMin, endMin })
      }
    }
    return blocks
  }

  const preferredBlocks = buildCellBlocks(customerPreferredCells)
  const allSelectedBlocks = buildCellBlocks(selectedLessonCells)
  const availabilityEditBlocks = mode === 'availability' ? buildCellBlocks(availabilityCells) : []

  const lessonBlocks = (() => {
    if (!calendarData) return []
    const blocks = []
    for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
      let currentLesson = null
      let blockStartMin = null
      for (const slot of slots) {
        const lesson = getLessonForSlot(dayIndex, slot.minutes)
        if (lesson && (!currentLesson || lesson.start_time === currentLesson.start_time)) {
          if (blockStartMin === null) blockStartMin = slot.minutes
          currentLesson = lesson
        } else {
          if (blockStartMin !== null) {
            const startIdx = slots.findIndex((s) => s.minutes === blockStartMin)
            blocks.push({ dayIndex, startIdx, slotCount: (slot.minutes - blockStartMin) / 15, startMin: blockStartMin, endMin: slot.minutes, lesson: currentLesson, outsideAvail: !isTutoringSlot(dayIndex, blockStartMin) })
            blockStartMin = null
            currentLesson = null
          }
          if (lesson) {
            blockStartMin = slot.minutes
            currentLesson = lesson
          }
        }
      }
      if (blockStartMin !== null) {
        const startIdx = slots.findIndex((s) => s.minutes === blockStartMin)
        const endMin = slots[slots.length - 1].minutes + 15
        blocks.push({ dayIndex, startIdx, slotCount: (endMin - blockStartMin) / 15, startMin: blockStartMin, endMin, lesson: currentLesson, outsideAvail: !isTutoringSlot(dayIndex, blockStartMin) })
      }
    }
    return blocks
  })()

  function getBlockScheme(dayIndex, startMin, endMin) {
    for (let m = startMin; m < endMin; m += 15) {
      if (!isTutoringSlot(dayIndex, m)) return 'orange'
    }
    return 'teal'
  }

  function splitAndColorBlock(block) {
    const cells = []
    for (let m = block.startMin; m < block.endMin; m += 15) {
      cells.push({ m, scheme: !isTutoringSlot(block.dayIndex, m) ? 'orange' : 'teal' })
    }
    const rawSegments = []
    let segStart = 0
    for (let i = 1; i <= cells.length; i++) {
      if (i === cells.length || cells[i].scheme !== cells[segStart].scheme) {
        rawSegments.push({ startMin: cells[segStart].m, endMin: i < cells.length ? cells[i].m : block.endMin, scheme: cells[segStart].scheme, duration: (i - segStart) * 15 })
        segStart = i
      }
    }
    if (rawSegments.length <= 1) {
      return [{ dayIndex: block.dayIndex, startMin: block.startMin, endMin: block.endMin, scheme: rawSegments[0]?.scheme ?? 'teal' }]
    }
    const groups = []
    let currentGroup = [0]
    let currentDuration = rawSegments[0].duration
    for (let i = 1; i < rawSegments.length; i++) {
      const remainingDuration = rawSegments.slice(i).reduce((sum, s) => sum + s.duration, 0)
      if (currentDuration >= lessonDuration && remainingDuration >= lessonDuration) {
        groups.push(currentGroup)
        currentGroup = [i]
        currentDuration = rawSegments[i].duration
      } else {
        currentGroup.push(i)
        currentDuration += rawSegments[i].duration
      }
    }
    groups.push(currentGroup)
    return groups.map((group) => {
      const segs = group.map((i) => rawSegments[i])
      const startMin = segs[0].startMin
      const endMin = segs[segs.length - 1].endMin
      const hasOrange = segs.some((s) => s.scheme === 'orange')
      return { dayIndex: block.dayIndex, startMin, endMin, scheme: hasOrange ? 'orange' : 'teal' }
    })
  }

  const splitBlocks = allSelectedBlocks.flatMap((block) =>
    splitAndColorBlock(block).map((split) => {
      const startIdx = slots.findIndex((s) => s.minutes === split.startMin)
      return { ...split, startIdx, slotCount: (split.endMin - split.startMin) / 15, warning: split.scheme === 'orange' }
    }),
  )

  const displayPreferredBlocks = splitBlocks.map((block) => {
    const key = block.dayIndex + ':' + block.startMin
    if (dragging && block.dayIndex + 1 === dragging.dayOrdinal && block.startMin === dragging.originalStartMin && block.endMin === dragging.originalEndMin) {
      const startIdx = slots.findIndex((s) => s.minutes === dragging.currentStartMin)
      const scheme = getBlockScheme(block.dayIndex, dragging.currentStartMin, dragging.currentEndMin)
      const overlapsLesson = dragging.edge === 'move' && lessonBlocks.some((lb) => lb.dayIndex === block.dayIndex && lb.startMin < dragging.currentEndMin && lb.endMin > dragging.currentStartMin)
      return { ...block, key, startIdx, slotCount: (dragging.currentEndMin - dragging.currentStartMin) / 15, startMin: dragging.currentStartMin, endMin: dragging.currentEndMin, scheme: overlapsLesson ? 'red' : scheme, warning: scheme === 'orange', overlapsLesson, isDragging: true }
    }
    return { ...block, key }
  })

  const displayAvailabilityBlocks = availabilityEditBlocks.map((block) => {
    const key = block.dayIndex + ':' + block.startMin
    if (dragging && block.dayIndex + 1 === dragging.dayOrdinal && block.startMin === dragging.originalStartMin && block.endMin === dragging.originalEndMin) {
      const startIdx = slots.findIndex((s) => s.minutes === dragging.currentStartMin)
      return { ...block, key, startIdx, slotCount: (dragging.currentEndMin - dragging.currentStartMin) / 15, startMin: dragging.currentStartMin, endMin: dragging.currentEndMin }
    }
    return { ...block, key }
  })

  function buildGaps(predicate, minSize) {
    const gaps = []
    for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
      let gapStartMin = null
      for (const slot of slots) {
        if (predicate(dayIndex, slot.minutes)) {
          if (gapStartMin === null) gapStartMin = slot.minutes
        } else if (gapStartMin !== null) {
          if (slot.minutes - gapStartMin >= minSize) {
            const startIdx = slots.findIndex((s) => s.minutes === gapStartMin)
            gaps.push({ dayIndex, startIdx, slotCount: (slot.minutes - gapStartMin) / 15, startMin: gapStartMin, endMin: slot.minutes })
          }
          gapStartMin = null
        }
      }
      if (gapStartMin !== null) {
        const endMin = slots[slots.length - 1].minutes + 15
        if (endMin - gapStartMin >= minSize) {
          const startIdx = slots.findIndex((s) => s.minutes === gapStartMin)
          gaps.push({ dayIndex, startIdx, slotCount: (endMin - gapStartMin) / 15, startMin: gapStartMin, endMin })
        }
      }
    }
    return gaps
  }

  const availabilityGaps = mode === 'availability' ? buildGaps((d, m) => !availabilityCells.has(cellKey(d + 1, m)), 45) : []
  const allClickableGaps = mode === 'lesson' ? buildGaps((d, m) => !isLessonSlot(d, m) && !selectedLessonCells.has(cellKey(d + 1, m)), lessonDuration) : []

  const dragIndicators = (() => {
    if (!dragging) return []
    const indicators = []
    if (dragging.edge === 'top' || dragging.edge === 'move') indicators.push(dragging.currentStartMin)
    if (dragging.edge === 'bottom' || dragging.edge === 'move') indicators.push(dragging.currentEndMin)
    return indicators
  })()

  const dragIndicatorColor = (() => {
    if (!dragging || mode === 'availability') return 'bg-emerald-500'
    const dayIndex = dragging.dayOrdinal - 1
    const overlapsLesson = dragging.edge === 'move' && lessonBlocks.some((lb) => lb.dayIndex === dayIndex && lb.startMin < dragging.currentEndMin && lb.endMin > dragging.currentStartMin)
    if (overlapsLesson) return 'bg-red-400'
    const scheme = getBlockScheme(dayIndex, dragging.currentStartMin, dragging.currentEndMin)
    if (scheme === 'orange') return 'bg-amber-400'
    return 'bg-emerald-500'
  })()

  // keep latest derived context for the window-bound drag handlers
  ctxRef.current = { mode, lessonBlocks, splitBlocks, availabilityEditBlocks, visibleRange, lessonDuration, singleDayIndex, isTutoringSlot }

  // ---- Interaction ---------------------------------------------------------
  function handlePreferredBlockClick(block, event) {
    if (didDragRef.current) return
    const dayOrdinal = block.dayIndex + 1
    const blockDuration = mode === 'availability' ? Math.min(60, block.endMin - block.startMin) : lessonDuration
    let startMin = block.startMin
    if (event && gridRef.current) {
      const rect = gridRef.current.getBoundingClientRect()
      const clientY = event.touches ? event.touches[0].clientY : event.clientY
      const y = clientY - rect.top - 5
      const clickedMin = visibleRange.start + Math.round(y / 16) * 15
      startMin = Math.max(block.startMin, Math.min(clickedMin, block.endMin - blockDuration))
    }
    const endMin = Math.min(startMin + blockDuration, block.endMin)
    const keys = []
    for (let m = startMin; m < endMin; m += 15) {
      if (mode === 'availability' || !isLessonSlot(block.dayIndex, m)) keys.push(cellKey(dayOrdinal, m))
    }
    if (keys.length === 0) return
    cbRef.current.onSelectPreferredBlock?.({ dayOrdinal, keys, selected: true })
  }

  function handleRemoveAvailableBlock(block) {
    const dayOrdinal = block.dayIndex + 1
    const keys = []
    for (let m = block.startMin; m < block.endMin; m += 15) keys.push(cellKey(dayOrdinal, m))
    cbRef.current.onSelectPreferredBlock?.({ dayOrdinal, keys, selected: false })
  }

  function handleDragStart(edge, block, event = null) {
    if (edge === 'move') {
      const durationMin = block.endMin - block.startMin
      const maxDragMin = singleDayIndex !== null ? 180 : 360
      if (durationMin > maxDragMin) return
    }
    let boundStartMin = visibleRange.start
    let boundEndMin = visibleRange.end
    if (mode === 'availability') {
      const siblings = availabilityEditBlocks.filter((ab) => ab.dayIndex === block.dayIndex && (ab.startMin !== block.startMin || ab.endMin !== block.endMin))
      for (const sib of siblings) {
        if (sib.endMin <= block.startMin && sib.endMin > boundStartMin) boundStartMin = sib.endMin
        if (sib.startMin >= block.endMin && sib.startMin < boundEndMin) boundEndMin = sib.startMin
      }
    } else {
      if (edge !== 'move') {
        for (const lb of lessonBlocks) {
          if (lb.dayIndex !== block.dayIndex) continue
          if (lb.endMin <= block.startMin && lb.endMin > boundStartMin) boundStartMin = lb.endMin
          if (lb.startMin >= block.endMin && lb.startMin < boundEndMin) boundEndMin = lb.startMin
        }
      }
      const siblings = splitBlocks.filter((ab) => ab.dayIndex === block.dayIndex && (ab.startMin !== block.startMin || ab.endMin !== block.endMin))
      for (const sib of siblings) {
        if (sib.endMin <= block.startMin && sib.endMin > boundStartMin) boundStartMin = sib.endMin
        if (sib.startMin >= block.endMin && sib.startMin < boundEndMin) boundEndMin = sib.startMin
      }
    }
    let dragOffset = 0
    if (edge === 'move' && event && gridRef.current) {
      const rect = gridRef.current.getBoundingClientRect()
      const clientY = event.touches ? event.touches[0].clientY : event.clientY
      const y = clientY - rect.top
      const mouseMinutes = visibleRange.start + Math.round(y / 16) * 15
      dragOffset = mouseMinutes - block.startMin
    }
    didDragRef.current = false
    setDragging({
      edge,
      dayOrdinal: block.dayIndex + 1,
      boundStartMin,
      boundEndMin,
      originalStartMin: block.startMin,
      originalEndMin: block.endMin,
      currentStartMin: block.startMin,
      currentEndMin: block.endMin,
      dragOffset,
    })
  }

  React.useEffect(() => {
    const handleDragMove = (event) => {
      const d = draggingRef.current
      if (!d || !gridRef.current) return
      didDragRef.current = true
      const { mode: m, visibleRange: vr, lessonDuration: ld } = ctxRef.current
      const clientY = event.touches ? event.touches[0].clientY : event.clientY
      const rect = gridRef.current.getBoundingClientRect()
      const y = clientY - rect.top
      const rawMinutes = vr.start + Math.round(y / 16) * 15
      const minBlockSize = m === 'availability' ? 45 : ld
      if (d.edge === 'move') {
        const duration = d.currentEndMin - d.currentStartMin
        let newStart = rawMinutes - d.dragOffset
        newStart = Math.max(d.boundStartMin, Math.min(newStart, d.boundEndMin - duration))
        setDragging({ ...d, currentStartMin: newStart, currentEndMin: newStart + duration })
      } else if (d.edge === 'top') {
        const clamped = Math.max(d.boundStartMin, Math.min(rawMinutes, d.currentEndMin - minBlockSize))
        setDragging({ ...d, currentStartMin: clamped })
      } else {
        const clamped = Math.min(d.boundEndMin, Math.max(rawMinutes, d.currentStartMin + minBlockSize))
        setDragging({ ...d, currentEndMin: clamped })
      }
    }
    const handleMouseUp = () => {
      const d = draggingRef.current
      if (!d) return
      const { mode: m, lessonBlocks: lbs, isTutoringSlot: its } = ctxRef.current
      const dayIndex = d.dayOrdinal - 1
      const overlapsLesson = d.edge === 'move' && m === 'lesson' && lbs.some((lb) => lb.dayIndex === dayIndex && lb.startMin < d.currentEndMin && lb.endMin > d.currentStartMin)
      const finalStartMin = overlapsLesson ? d.originalStartMin : d.currentStartMin
      const finalEndMin = overlapsLesson ? d.originalEndMin : d.currentEndMin
      cbRef.current.onResizePreferredBlock?.({ dayOrdinal: d.dayOrdinal, oldStartMin: d.originalStartMin, oldEndMin: d.originalEndMin, newStartMin: finalStartMin, newEndMin: finalEndMin })
      if (m === 'lesson' && !overlapsLesson) {
        let hasOutside = false
        for (let mm = finalStartMin; mm < finalEndMin; mm += 15) {
          if (!its(dayIndex, mm)) { hasOutside = true; break }
        }
        if (hasOutside) cbRef.current.onOutsideAvailability?.()
      }
      setDragging(null)
      setTimeout(() => { didDragRef.current = false }, 0)
    }
    const handleTouchMove = (event) => {
      if (!draggingRef.current) return
      event.preventDefault()
      handleDragMove(event)
    }
    const handleCancel = () => { setDragging(null); didDragRef.current = false }
    window.addEventListener('mousemove', handleDragMove)
    window.addEventListener('mouseup', handleMouseUp)
    window.addEventListener('touchmove', handleTouchMove, { passive: false })
    window.addEventListener('touchend', handleMouseUp)
    window.addEventListener('touchcancel', handleCancel)
    return () => {
      window.removeEventListener('mousemove', handleDragMove)
      window.removeEventListener('mouseup', handleMouseUp)
      window.removeEventListener('touchmove', handleTouchMove)
      window.removeEventListener('touchend', handleMouseUp)
      window.removeEventListener('touchcancel', handleCancel)
    }
  }, [])

  if (!calendarData) return null

  // ---- Reusable block fragments --------------------------------------------
  const warnTriangle = (cls) => (
    <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
  )
  const closeX = (cls) => (
    <svg className={cls} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" /></svg>
  )
  const eventLeft = (dayIndex) => `calc(64px + ${dayIndex} * (100% - 64px) / 7)`
  const eventWidth = 'calc(((100% - 64px) / 7 - 3px) * 0.8 + 2px)'

  const renderLessonBlock = (block, idx, faint) => (
    <div
      key={`${faint ? 'lo' : 'l'}${idx}`}
      className={'absolute ' + (faint ? 'z-[30] pointer-events-none opacity-20' : 'z-[15] ' + (mode === 'availability' ? 'pointer-events-none opacity-60' : 'cursor-not-allowed'))}
      style={{ top: block.startIdx * 16, height: block.slotCount * 16 - 1, left: eventLeft(block.dayIndex), width: eventWidth }}
    >
      <div className="flex h-full w-full flex-col items-start justify-start overflow-hidden rounded-lg border border-gray-300 bg-gray-100 px-2 py-2.5">
        <span className="text-[11px] font-semibold leading-tight text-gray-400">Lesson</span>
        {block.endMin - block.startMin >= 45 ? (
          <span className="text-[10px] leading-tight text-gray-400">{fmtMin12(block.startMin)} – {fmtMin12(block.endMin)}</span>
        ) : null}
      </div>
    </div>
  )

  return (
    <div className="select-none">
      {/* Sticky header: legend (availability mode only) + day labels */}
      <div className="sticky top-0 z-[35] bg-white">
        <div className="py-2">
          {mode === 'availability' ? (
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-400">
              <span className="inline-flex items-center gap-1.5"><span className="h-3.5 w-3.5 shrink-0 rounded border border-emerald-500 bg-emerald-300" />{tutorName ? `${tutorName}'s profile availability` : 'Profile availability'}</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-3.5 w-3.5 shrink-0 rounded border border-gray-300 bg-gray-100" />Existing lesson</span>
            </div>
          ) : null}
        </div>
        {canExpandEarlier ? (
          <button onClick={() => setExpandTop((v) => v + 180)} className="w-full py-2 text-xs font-semibold text-gray-400 underline underline-offset-2 transition-colors hover:text-gray-600">Show earlier ▲</button>
        ) : null}
        <div className="grid grid-cols-[64px_repeat(7,1fr)]">
          <div className="bg-white" />
          {dayLabels.map((label, i) => (
            <div key={label} className={'flex flex-col items-center justify-center border-b border-l border-t border-gray-200 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500 ' + (mode === 'availability' ? 'bg-gray-50/60' : 'bg-white') + (i === 6 ? ' border-r' : '')}>
              {label}
            </div>
          ))}
        </div>
      </div>

      {/* Grid body */}
      <div className="stripe-unavailable relative" ref={gridRef}>
        <div className="pointer-events-none absolute inset-y-0 right-0 z-[3] border-b border-l border-r border-gray-200" style={{ left: 64 }} />

        {/* Layer 1: background grid (white where tutor is available) */}
        <div className="relative z-[2]">
          {slots.map((slot, slotIdx) => (
            <div key={slot.minutes} className="grid grid-cols-[64px_repeat(7,1fr)]">
              <div className="flex h-[16px] items-start justify-start overflow-visible bg-white pl-1">
                {slot.isHour ? (
                  <span className={'text-[11px] font-semibold leading-none text-gray-500' + (slotIdx === 0 ? '' : ' -translate-y-1/2')}>{fmtMin12(slot.minutes)}</span>
                ) : null}
              </div>
              {dayLabels.map((_, dayIndex) => {
                const outsideAvail = mode === 'availability' ? true : !tutoringCells.has(`${dayIndex}:${slot.minutes}`)
                const inPreferred = mode === 'lesson' && customerPreferredCells.has(cellKey(dayIndex + 1, slot.minutes))
                const inPreferredMid = inPreferred && customerPreferredCells.has(cellKey(dayIndex + 1, slot.minutes - 15))
                const fadeLeft = mode === 'availability' ? true : outsideAvail && (dayIndex === 0 || !tutoringCells.has(`${dayIndex - 1}:${slot.minutes}`))
                const fadeTop = mode === 'availability' ? true : outsideAvail && (slotIdx === 0 || !tutoringCells.has(`${dayIndex}:${slot.minutes - 15}`))
                const cls =
                  'h-[16px] border-l ' +
                  (fadeLeft ? 'border-black/[0.03]' : 'border-black/10') +
                  (outsideAvail ? '' : ' bg-white') +
                  (slotIdx === 0
                    ? ' border-t border-t-black/10'
                    : slot.isHour && !inPreferredMid
                      ? fadeTop
                        ? ' border-t border-t-black/[0.03]'
                        : ' border-t border-t-black/10'
                      : '') +
                  (slotIdx === slots.length - 1 ? ' border-b border-b-black/10' : '')
                return <div key={dayIndex} className={cls} />
              })}
            </div>
          ))}
        </div>

        {endLabel ? (
          <span className="absolute bottom-0 left-1 z-[4] translate-y-[2px] bg-white text-[11px] font-semibold leading-none text-gray-500">{endLabel}</span>
        ) : null}

        {/* Layer 2: client preferred (z-10, visual only) */}
        {mode === 'lesson' || customerPreferredCells.size > 0
          ? preferredBlocks.map((block, idx) => (
              <div
                key={`p${idx}`}
                className="stripe-purple pointer-events-none absolute z-10 flex flex-col items-start justify-start border border-dashed border-purple-300/60 bg-purple-100/50 pl-2 pt-2"
                style={{ top: block.startIdx * 16, height: block.slotCount * 16, left: eventLeft(block.dayIndex), width: 'calc((100% - 64px) / 7)' }}
              >
                <span className="text-[10px] font-semibold leading-none text-purple-600">★ Client Prefers</span>
                <span className="mt-0.5 text-[10px] font-medium leading-none text-purple-600">{fmtMin12(block.startMin)}–{fmtMin12(block.endMin)}</span>
              </div>
            ))
          : null}

        {/* Lessons (z-[15]) */}
        {lessonBlocks.map((block, idx) => renderLessonBlock(block, idx, false))}

        {/* Lesson mode: selected lesson blocks + clickable gaps */}
        {mode === 'lesson'
          ? displayPreferredBlocks.map((block) => {
              const s = block.scheme
              const borderBg = s === 'red' ? 'border-red-500 bg-red-100' : s === 'orange' ? 'border-amber-500 bg-amber-100' : 'border-emerald-500 bg-emerald-300'
              return (
                <div key={block.key} className={'absolute z-[28] rounded-lg ' + (block.isDragging && s === 'red' ? 'opacity-60' : '')} style={{ top: block.startIdx * 16, height: block.slotCount * 16 - 1, left: eventLeft(block.dayIndex), width: eventWidth }}>
                  <div className={'relative h-full w-full rounded-lg border px-2 py-2.5 flex flex-col overflow-hidden ' + borderBg + (s === 'red' ? ' items-center justify-center' : ' items-start justify-start')}>
                    {s === 'red' ? (
                      <svg className="h-4 w-4 shrink-0 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>
                    ) : s === 'orange' ? (
                      block.slotCount > 3 ? (
                        <>
                          {warnTriangle('h-4 w-4 shrink-0 text-amber-500')}
                          <span className="text-[11px] font-semibold leading-tight text-amber-600">See below</span>
                        </>
                      ) : (
                        <span className="flex items-center gap-0.5 text-[11px] font-semibold leading-tight text-amber-600">
                          {warnTriangle('h-3.5 w-3.5 shrink-0 text-amber-500')}
                          See below
                        </span>
                      )
                    ) : (
                      <span className="text-[11px] font-semibold leading-tight text-emerald-800">Available</span>
                    )}
                    {s === 'orange' && block.slotCount > 3 ? (
                      <span className="text-[10px] leading-tight text-amber-700">{fmtMin12(block.startMin)} – {fmtMin12(block.endMin)}</span>
                    ) : s !== 'orange' && s !== 'red' ? (
                      <span className="text-[10px] leading-tight text-emerald-800">{fmtMin12(block.startMin)} – {fmtMin12(block.endMin)}</span>
                    ) : null}
                    <button
                      type="button"
                      className={'absolute right-0.5 top-0.5 z-[3] rounded-full p-0.5 transition-colors ' + (s === 'red' ? 'text-red-400 hover:bg-red-300/50' : s === 'orange' ? 'text-amber-500 hover:bg-amber-300/50' : 'text-emerald-600 hover:bg-emerald-500')}
                      onClick={(e) => { e.stopPropagation(); handleRemoveAvailableBlock(block) }}
                    >
                      {closeX('h-3.5 w-3.5')}
                    </button>
                    <div className="absolute left-0 right-0 top-0 z-[2] flex h-2 cursor-ns-resize items-center justify-center" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleDragStart('top', block) }}>
                      <span className={'h-[2px] w-4 rounded-full ' + (s === 'red' ? 'bg-red-400/40' : s === 'orange' ? 'bg-amber-400/40' : 'bg-emerald-600/40')} />
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 z-[2] flex h-2 cursor-ns-resize items-center justify-center" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleDragStart('bottom', block) }}>
                      <span className={'h-[2px] w-4 rounded-full ' + (s === 'red' ? 'bg-red-400/40' : s === 'orange' ? 'bg-amber-400/40' : 'bg-emerald-600/40')} />
                    </div>
                    <div className={'absolute inset-0 z-[1] rounded-lg ' + ((block.endMin - block.startMin) > (singleDayIndex !== null ? 180 : 360) ? 'cursor-default' : 'cursor-grab')} onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleDragStart('move', block, e) }} />
                  </div>
                </div>
              )
            })
          : null}

        {mode === 'lesson'
          ? allClickableGaps.map((gap, idx) => (
              <div key={`cg${idx}`} className="absolute z-[26] cursor-pointer" style={{ top: gap.startIdx * 16 + 1, height: gap.slotCount * 16 - 1, left: `calc(64px + ${gap.dayIndex} * (100% - 64px) / 7 + 1px)`, width: 'calc((100% - 64px) / 7 - 1px)' }} onClick={(e) => { e.stopPropagation(); handlePreferredBlockClick(gap, e) }} />
            ))
          : null}

        {/* Availability mode: editable blocks + gaps + faint lessons overlay */}
        {mode === 'availability'
          ? displayAvailabilityBlocks.map((block) => (
              <div key={block.key} className="absolute z-[28] rounded-lg" style={{ top: block.startIdx * 16, height: block.slotCount * 16 - 1, left: eventLeft(block.dayIndex), width: eventWidth }}>
                <div className="relative flex h-full w-full flex-col items-start justify-start overflow-hidden rounded-lg border border-emerald-500 bg-emerald-300 px-2 py-2.5">
                  <span className="text-[11px] font-semibold leading-tight text-emerald-800">Available</span>
                  <span className="text-[10px] leading-tight text-emerald-800">{fmtMin12(block.startMin)} – {fmtMin12(block.endMin)}</span>
                  <button type="button" className="absolute right-1 top-1 z-[3] rounded-full p-0.5 text-emerald-600 transition-colors hover:bg-emerald-500" onClick={(e) => { e.stopPropagation(); handleRemoveAvailableBlock(block) }}>
                    {closeX('h-3.5 w-3.5')}
                  </button>
                  <div className="absolute left-0 right-0 top-0 z-[2] flex h-2 cursor-ns-resize items-center justify-center" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleDragStart('top', block) }}>
                    <span className="h-[2px] w-4 rounded-full bg-emerald-600/40" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 z-[2] flex h-2 cursor-ns-resize items-center justify-center" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleDragStart('bottom', block) }}>
                    <span className="h-[2px] w-4 rounded-full bg-emerald-600/40" />
                  </div>
                  <div className={'absolute inset-0 z-[1] rounded-lg ' + ((block.endMin - block.startMin) > (singleDayIndex !== null ? 180 : 360) ? 'cursor-default' : 'cursor-grab')} onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleDragStart('move', block, e) }} />
                </div>
              </div>
            ))
          : null}

        {mode === 'availability'
          ? availabilityGaps.map((gap, idx) => (
              <div key={`ag${idx}`} className="absolute z-[26] cursor-pointer" style={{ top: gap.startIdx * 16 + 1, height: gap.slotCount * 16 - 1, left: `calc(64px + ${gap.dayIndex} * (100% - 64px) / 7 + 1px)`, width: 'calc((100% - 64px) / 7 - 1px)' }} onClick={(e) => { e.stopPropagation(); handlePreferredBlockClick(gap, e) }} />
            ))
          : null}

        {mode === 'availability' ? lessonBlocks.map((block, idx) => renderLessonBlock(block, idx, true)) : null}

        {/* Drag time indicators (z-[40]) */}
        {dragging ? (
          <>
            <div className={'pointer-events-none absolute inset-y-0 left-0 z-[1] w-[64px] ' + (dragIndicatorColor === 'bg-red-400' ? 'bg-red-50' : dragIndicatorColor === 'bg-amber-400' ? 'bg-amber-50' : 'bg-emerald-50')} />
            {dragIndicators.map((minutes, i) => {
              const y = ((minutes - visibleRange.start) / 15) * 16
              return (
                <div key={i} className={'pointer-events-none absolute z-[40] flex items-center rounded-r-full pl-1 pr-1.5 ' + dragIndicatorColor} style={{ top: y, left: 0, height: 14, transform: 'translateY(-50%)' }}>
                  <span className="text-[10px] font-semibold leading-none text-white">{fmtMin12(minutes)}</span>
                </div>
              )
            })}
          </>
        ) : null}
      </div>

      {canExpandLater ? (
        <button onClick={() => setExpandBottom((v) => v + 180)} className="w-full py-3 text-xs font-semibold text-gray-400 underline underline-offset-2 transition-colors hover:text-gray-600">Show later ▼</button>
      ) : null}
    </div>
  )
}

export default AvailabilityCalendar
